'use client'

import { createContext, type ReactNode, useContext, useMemo } from 'react'

import { usePage } from '@/components/providers/PageProvider'
import { type GalleryGroupType } from '@/defs/gallery'
import { type RecRoleType, type RecType } from '@/defs/rec'
import { useCardMap } from '@/hooks/useCardMap'
import { usePriceSources } from '@/hooks/usePriceSources'
import { useReduxSelector } from '@/store/hooks'
import { isNonEmptyString } from '@/utils/commonUtil'
import { getGalleryResults, isGalleryCardRequired } from '@/utils/galleryUtil'

import { useGalleryDisplay } from '../GalleryDisplayProvider/GalleryDisplayProvider'
import { useGalleryPreload } from '../GalleryPreloadProvider/GalleryPreloadProvider'

type GalleryGroupsProviderType = {
  filteredCount: number
  groups: GalleryGroupType[]
  hasError?: boolean
  hasMultipleRoles: boolean
  isEmpty?: boolean
  loading?: boolean
  paginationTotal?: number
  recsCount: number
  role?: RecRoleType
  roles: RecRoleType[]
}

type Props = {
  children: ReactNode
  recs?: RecType[]
}

const DEFAULT_GROUPS_PROVIDER: GalleryGroupsProviderType = {
  filteredCount: 0,
  groups: [],
  hasMultipleRoles: false,
  recsCount: 0,
  roles: [],
}

const GalleryGroupsContext = createContext<GalleryGroupsProviderType>(DEFAULT_GROUPS_PROVIDER)

export const GalleryGroupsProvider = ({ children, recs: recsProp }: Props) => {
  const { page } = usePage()
  const { preload } = useGalleryPreload()
  const gallery = page?.gallery
  const categories = gallery?.categories
  const paginationTotal = gallery?.paginationTotal
  const recs = useMemo(() => [...(gallery?.recs || []), ...(recsProp || [])], [gallery, recsProp])

  const { groupby, sortby, sortbyDirection } = useGalleryDisplay()

  const priceSource = usePriceSources()[0]
  const cardFilters = useReduxSelector((s) => s.gallery.cardFilters)
  const rolePref = useReduxSelector((s) => s.gallery.rolePref)

  const isCardMapRequired = isGalleryCardRequired({
    cardFilters,
    groupby,
    sortby,
  })
  const cardIds = useMemo(() => recs.flatMap((r) => [r.cardId, r.partnerRef?.cardId].filter(isNonEmptyString)), [recs])

  // Silently preload the same card map if triggered
  useCardMap({ cardIds, enabled: preload })
  const { cardMap, hasError, loading: loadingCardMap } = useCardMap({ cardIds, enabled: isCardMapRequired })
  const loading = isCardMapRequired && loadingCardMap

  const applied = useMemo(
    () =>
      getGalleryResults({
        cardFilters,
        cardMap,
        categories,
        groupby,
        isRoleFiltered: true,
        priceSource,
        recs,
        rolePref,
        sortby,
        sortbyDirection,
      }),
    [cardFilters, cardMap, categories, groupby, priceSource, recs, rolePref, sortby, sortbyDirection],
  )

  const data = useMemo(() => {
    const isEmpty = applied.groups.every((g) => g.recs.length < 1)

    return { ...applied, hasError, isEmpty, loading }
  }, [applied, hasError, loading])

  const value = useMemo(() => ({ ...data, paginationTotal }), [data, paginationTotal])

  return <GalleryGroupsContext.Provider value={value}>{children}</GalleryGroupsContext.Provider>
}

export const useGalleryGroups = (): GalleryGroupsProviderType => useContext(GalleryGroupsContext)
